// Create a namespace for our vehicle search functionality
window.VehicleSearchApp = window.VehicleSearchApp || {
    api: null,
    settings: {
        preventRedirect: false,
        isRegSearchEnabled: true
    },
    initialized: false,
    eventListenersAttached: false,
    dropdownsInitialized: false,
    elements: {}, // Store DOM elements here
    dropdownsInitializing: false // Add flag to track initialization in progress
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeVehicleSearch);
} else {
    initializeVehicleSearch();
}

// Re-initialize when sections are reloaded (for theme editor)
document.addEventListener('shopify:section:load', initializeVehicleSearch);

// Main initialization function
async function initializeVehicleSearch() {
    // Check if already initialized
    if (window.VehicleSearchApp.initialized) {
        return;
    }
    
    // Get the vehicle search modal element
    const vehicleSearch = document.querySelector('[data-vehicle-search-flyout]');
    if (!vehicleSearch) {
        return;
    }

    // Store elements in the app namespace
    window.VehicleSearchApp.elements = {
        vehicleSearch,
        desktopTriggers: document.querySelectorAll('[data-vehicle-search-trigger]'),
        mobileTrigger: document.querySelector('[data-vehicle-search-trigger-mobile]'),
        closeButtons: document.querySelectorAll('[data-vehicle-search-close]'),
        tabButtons: document.querySelectorAll('.tab-button'),
        savedVehiclesList: document.getElementById('saved-vehicles-list'),
        emptyMessage: document.getElementById('empty-vehicles-message')
    };

    // Check if mobile
    const isMobile = window.innerWidth <= 768;
    vehicleSearch.classList.add(isMobile ? 'vehicle-search--mobile' : 'vehicle-search--desktop');

    // Set up tab switching
    const tabButtons = document.querySelectorAll('.tab-button');
    tabButtons.forEach(button => {
        button.addEventListener('click', function() {
            const tabId = this.getAttribute('data-tab');
            switchTab(tabId);
        });
    });
    
    // Only set up event listeners and basic structure
    // Don't load dropdown data yet
    addListeners();
    
    // Initialize registration lookup
    initializeRegLookup();
    
    // Mark as initialized but don't load dropdowns
    window.VehicleSearchApp.initialized = true;
    
    // Update number bubbles for initial state
    updateNumberBubbles();
}

async function initializeDropdowns() {
    try {
        // Wait for API to be ready
        if (!window.VehicleSearchApp.api) {
            await new Promise((resolve) => {
                document.addEventListener('vehicle-search-api-ready', resolve, { once: true });
            });
        }

        const makeSelect = document.getElementById('make');
        if (!makeSelect) {
            return;
        }

        // Use cached makes if available
        if (window.VehicleSearchApp.makes) {
            populateDropdown(makeSelect, window.VehicleSearchApp.makes, 'Select Make');
            makeSelect.disabled = false;
            return;
        }

        // Get all unique makes from the API
        const fitments = await window.VehicleSearchApp.api.ProductVehicleFitment.findMany({
            first: 250,
            select: { make: true },
            filter: { shopId: { equals: window.currentShopId.toString() } }
        });

        const makes = [...new Set(fitments.map(f => f.make))].filter(Boolean).sort();

        // Cache the makes data
        window.VehicleSearchApp.makes = makes;

        populateDropdown(makeSelect, makes, 'Select Make');
        makeSelect.disabled = false;

        // Attach change handlers
        attachDropdownHandlers();
    } catch (error) {
        throw error;
    }
}

// Modify the openVehicleSearch function to handle API initialization better
async function openVehicleSearch(e) {
    if (e) {
        e.preventDefault();
        e.stopPropagation();
    }

    const vehicleSearch = document.querySelector('[data-vehicle-search-flyout]');
    if (!vehicleSearch) return;

    // Show the modal first
    vehicleSearch.classList.add('active');
    document.body.classList.add('vehicle-search-open');

    // Initialize dropdowns only if not already initialized or initializing
    if (!window.VehicleSearchApp.dropdownsInitialized && !window.VehicleSearchApp.dropdownsInitializing) {
        window.VehicleSearchApp.dropdownsInitializing = true;
        try {
            await initializeDropdowns();
            window.VehicleSearchApp.dropdownsInitialized = true;
        } catch (error) {
            // Don't close the modal on error, just show a user-friendly message
            const errorDiv = document.getElementById('reg-search-error');
            if (errorDiv) {
                errorDiv.textContent = 'There was an error loading vehicle data. Please try again later.';
            }
        } finally {
            window.VehicleSearchApp.dropdownsInitializing = false;
        }
    }
}

function attachDropdownHandlers() {
    const makeSelect = document.getElementById('make');
    const modelSelect = document.getElementById('model');
    const trimSelect = document.getElementById('trim');
    const cabTypeSelect = document.getElementById('cabType');
    const transmissionSelect = document.getElementById('transmission');
    const fuelTypeSelect = document.getElementById('fuelType');
    const yearSelect = document.getElementById('year');

    if (makeSelect) {
        makeSelect.addEventListener('change', async function() {
            const selectedMake = this.value;
            hideAllFields();
            resetAllDropdowns();

            if (!selectedMake) return;

            try {
                const makeFitments = await window.VehicleSearchApp.api.ProductVehicleFitment.findMany({
                    filter: {
                        AND: [
                            { shopId: { equals: window.currentShopId.toString() } },
                            { make: { equals: selectedMake } }
                        ]
                    },
                    select: {
                        model: true,
                        trim: true,
                        cabType: true,
                        transmission: true,
                        fuelType: true,
                        yearFrom: true,
                        yearTo: true
                    }
                });

                const models = [...new Set(makeFitments.map(f => f.model))].filter(Boolean).sort();
                if (models.length) {
                    showField('model-field');
                    populateDropdown(modelSelect, models, 'Select Model');
                    modelSelect.disabled = false;
                }
            } catch (error) {
                // Handle error silently
            }
        });
    }

    if (modelSelect) {
        modelSelect.addEventListener('change', async function() {
            const selectedMake = makeSelect.value;
            const selectedModel = this.value;
            hideFieldsAfter('model');
            resetDropdownsAfter('model');

            if (!selectedModel) return;

            try {
                const modelFitments = await window.VehicleSearchApp.api.ProductVehicleFitment.findMany({
                    filter: {
                        AND: [
                            { shopId: { equals: window.currentShopId.toString() } },
                            { make: { equals: selectedMake } },
                            { model: { equals: selectedModel } }
                        ]
                    },
                    select: {
                        trim: true,
                        cabType: true,
                        transmission: true,
                        fuelType: true,
                        yearFrom: true,
                        yearTo: true
                    }
                });

                // Process trims
                const trims = [...new Set(modelFitments.map(f => f.trim))].filter(Boolean).sort();
                if (trims.length) {
                    showField('trim-field');
                    populateDropdown(trimSelect, trims, 'Select Trim');
                    trimSelect.disabled = false;
                }

                // Process cab types
                const cabTypes = [...new Set(modelFitments.map(f => f.cabType))].filter(Boolean).sort();
                if (cabTypes.length) {
                    showField('cabType-field');
                    populateDropdown(cabTypeSelect, cabTypes, 'Select Cab Type');
                    cabTypeSelect.disabled = false;
                }

                // Process transmissions
                const transmissions = [...new Set(modelFitments.map(f => f.transmission))].filter(Boolean).sort();
                if (transmissions.length) {
                    showField('transmission-field');
                    populateDropdown(transmissionSelect, transmissions, 'Select Transmission');
                    transmissionSelect.disabled = false;
                }

                // Process fuel types
                const fuelTypes = [...new Set(modelFitments.map(f => f.fuelType))].filter(Boolean).sort();
                if (fuelTypes.length) {
                    showField('fuelType-field');
                    populateDropdown(fuelTypeSelect, fuelTypes, 'Select Fuel Type');
                    fuelTypeSelect.disabled = false;
                }

                // Always show year field last
                showField('year-field');
                const years = getYearRange(modelFitments);
                populateDropdown(yearSelect, years, 'Select Year');
                yearSelect.disabled = false;

            } catch (error) {
                // Handle error silently
            }
        });
    }

    // Add change handlers for other fields
    const additionalFields = ['trim', 'cabType', 'transmission', 'fuelType'];
    additionalFields.forEach(field => {
        const select = document.getElementById(field);
        if (select) {
            select.addEventListener('change', function() {
                if (this.value) {
                    showField('year-field');
                    yearSelect.disabled = false;
                }
            });
        }
    });

    if (yearSelect) {
        yearSelect.addEventListener('change', async function() {
            const selectedYear = this.value;
            if (!selectedYear) return;

            const vehicle = {
                make: makeSelect.value,
                model: modelSelect.value,
                trim: trimSelect?.value || null,
                cabType: cabTypeSelect?.value || null,
                transmission: transmissionSelect?.value || null,
                fuelType: fuelTypeSelect?.value || null,
                year: selectedYear
            };

            await handleVehicleMatch(vehicle);
        });
    }
}

// Helper functions for field management
function hideAllFields() {
    const fields = ['model-field', 'trim-field', 'cabType-field', 'transmission-field', 'fuelType-field', 'year-field'];
    fields.forEach(field => {
        const element = document.getElementById(field);
        if (element) element.style.display = 'none';
    });
    updateNumberBubbles(); // Update numbers after hiding fields
}

function hideFieldsAfter(field) {
    const fields = ['model-field', 'trim-field', 'cabType-field', 'transmission-field', 'fuelType-field', 'year-field'];
    const startIndex = fields.indexOf(field + '-field') + 1;
    
    for (let i = startIndex; i < fields.length; i++) {
        const element = document.getElementById(fields[i]);
        if (element) element.style.display = 'none';
    }
    updateNumberBubbles(); // Update numbers after hiding fields
}

function showField(fieldId) {
    const field = document.getElementById(fieldId);
    if (field) {
        field.style.display = 'block';
        updateNumberBubbles(); // Update numbers whenever a field is shown
    }
}

function resetAllDropdowns() {
    const fields = ['model', 'trim', 'cabType', 'transmission', 'fuelType', 'year'];
    fields.forEach(field => {
        const select = document.getElementById(field);
        if (select) {
            select.innerHTML = `<option value="">Select ${field.charAt(0).toUpperCase() + field.slice(1)}</option>`;
            select.disabled = true;
        }
    });
}

function resetDropdownsAfter(field) {
    const fields = ['model', 'trim', 'cabType', 'transmission', 'fuelType', 'year'];
    const startIndex = fields.indexOf(field) + 1;
    fields.slice(startIndex).forEach(f => {
        const select = document.getElementById(f);
        if (select) {
            select.innerHTML = `<option value="">Select ${f.charAt(0).toUpperCase() + f.slice(1)}</option>`;
            select.disabled = true;
        }
    });
}

function getYearRange(fitments) {
    const currentYear = new Date().getFullYear();
    const years = new Set();
    
    fitments.forEach(fitment => {
        if (fitment.yearFrom && fitment.yearTo) {
            for (let year = fitment.yearFrom; year <= fitment.yearTo; year++) {
                if (year <= currentYear) {
                    years.add(year.toString());
                }
            }
        }
    });

    return Array.from(years).sort((a, b) => b - a); // Sort descending
}

function initializeRegLookup() {
    const lookupButton = document.getElementById('lookupButton');
    const regInput = document.getElementById('registration');
    const errorDiv = document.getElementById('reg-search-error');

    if (!lookupButton || !regInput) {
        return;
    }

    lookupButton.addEventListener('click', async function(e) {
        e.preventDefault();
        const button = this;
        const originalText = button.textContent;

        try {
            button.disabled = true;
            button.textContent = 'Loading...';
            errorDiv.textContent = '';

            const registration = regInput.value.trim();
            
            if (!registration) {
                throw new Error('Please enter a registration number');
            }

            // Get API settings
            const settings = await window.VehicleSearchApp.api.UKVehicleAPISettings.findMany({
                first: 1,
                select: { apiKey: true },
                filter: { shopId: { equals: window.currentShopId.toString() } }
            });

            if (!settings?.[0]?.apiKey) {
                throw new Error('Vehicle lookup service not configured');
            }

            const response = await fetch(
                `https://api.checkcardetails.co.uk/vehicledata/vehiclespecs?apikey=${settings[0].apiKey}&vrm=${registration}`,
                { method: 'GET', headers: { 'Accept': 'application/json' } }
            );

            if (!response.ok) {
                showNoProductsMessage();
                return;
            }

            const data = await response.json();

            if (!data || !data.VehicleIdentification) {
                showNoProductsMessage();
                return;
            }

            const vehicleData = processVehicleData(data);

            if (!vehicleData.make || !vehicleData.model) {
                showNoProductsMessage();
                return;
            }

            // First try with exact make and model
            const fitmentResults = await window.VehicleSearchApp.api.ProductVehicleFitment.findMany({
                filter: {
                    AND: [
                        { shopId: { equals: window.currentShopId.toString() } },
                        { make: { equals: vehicleData.make.toUpperCase() } },
                        { model: { equals: vehicleData.model.charAt(0).toUpperCase() + vehicleData.model.slice(1) } }
                    ]
                },
                select: {
                    yearFrom: true,
                    yearTo: true,
                    tag: true,
                    trim: true,
                    cabType: true,
                    lowercaseTrim: true,
                    transmission: true,
                    fuelType: true,
                    collections: true,
                    customTags: true
                }
            });

            // If no results, try a more lenient search with just the make
            let lenientResults = [];
            if (fitmentResults.length === 0) {
                lenientResults = await window.VehicleSearchApp.api.ProductVehicleFitment.findMany({
                    filter: {
                        AND: [
                            { shopId: { equals: window.currentShopId.toString() } },
                            { make: { equals: vehicleData.make.toUpperCase() } }
                        ]
                    },
                    select: {
                        yearFrom: true,
                        yearTo: true,
                        tag: true,
                        trim: true,
                        model: true,
                        cabType: true,
                        lowercaseTrim: true,
                        transmission: true,
                        fuelType: true,
                        collections: true,
                        customTags: true
                    }
                });
            }

            // Combine and sort results, prioritizing those with transmission info
            const allResults = [...fitmentResults, ...lenientResults];
            const sortedFitments = allResults.sort((a, b) => (b.transmission ? 1 : 0) - (a.transmission ? 1 : 0));

            // Find matching fitment
            const matchingFitment = sortedFitments.find(f => matchFitment(vehicleData, f));

            if (!matchingFitment) {
                showNoProductsMessage();
                return;
            }

            // Save vehicle before navigation
            saveVehicle(vehicleData);

            // Navigate based on the matching fitment
            if (matchingFitment.collections && matchingFitment.collections.length > 0) {
                const collection = matchingFitment.collections[0];
                const collectionHandle = typeof collection === 'string' ? collection : collection.handle;
                window.location.href = `/collections/all/${encodeURIComponent(collectionHandle)}`;
                return;
            }

            if (matchingFitment.customTags && matchingFitment.customTags.length > 0) {
                const customTag = matchingFitment.customTags[0].replace(/\s+/g, '-').toLowerCase();
                window.location.href = `/collections/all/vehicle-${encodeURIComponent(customTag)}`;
                return;
            }

            if (matchingFitment.tag) {
                const cleanTag = matchingFitment.tag
                    .toLowerCase()
                    .replace(/\s+/g, '-')
                    .replace(/-+/g, '-')
                    .replace(/^-|-$/g, '');
                window.location.href = `/collections/all/${cleanTag}`;
                return;
            }

            const constructedTag = generateProductTag(vehicleData)
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
            window.location.href = `/collections/all/${constructedTag}`;

        } catch (error) {
            if (errorDiv) errorDiv.textContent = error.message || 'Failed to look up vehicle';
        } finally {
            button.disabled = false;
            button.textContent = originalText;
        }
    });
}

// Helper functions
function populateDropdown(select, items, defaultText = 'Select Option') {
    if (!select) return;
    select.innerHTML = `<option value="">${defaultText}</option>`;
    items.forEach(item => {
        const option = document.createElement('option');
        option.value = item;
        option.textContent = item;
        select.appendChild(option);
    });
}

async function handleVehicleMatch(vehicle) {
    try {
        const fitments = await window.VehicleSearchApp.api.ProductVehicleFitment.findMany({
            filter: {
                AND: [
                    { shopId: { equals: window.currentShopId.toString() } },
                    { make: { equals: vehicle.make } },
                    { model: { equals: vehicle.model } }
                ]
            },
            select: {
                yearFrom: true,
                yearTo: true,
                collections: true,
                customTags: true,
                tag: true,
                trim: true,
                cabType: true,
                transmission: true,
                fuelType: true,
                lowercaseTrim: true
            }
        });

        if (!fitments.length) {
            showNoProductsMessage();
            return;
        }

        // Find the matching fitment based on all criteria
        const matchingFitment = fitments.find(f => matchFitment(vehicle, f));

        if (!matchingFitment) {
            showNoProductsMessage();
            return;
        }

        // Save vehicle before navigation
        saveVehicle(vehicle);

        // Navigate based on the matching fitment
        if (matchingFitment.collections && matchingFitment.collections.length > 0) {
            const collection = matchingFitment.collections[0];
            const collectionHandle = typeof collection === 'string' ? collection : collection.handle;
            window.location.href = `/collections/all/${encodeURIComponent(collectionHandle)}`;
            return;
        }

        if (matchingFitment.customTags && matchingFitment.customTags.length > 0) {
            const customTag = matchingFitment.customTags[0].replace(/\s+/g, '-').toLowerCase();
            window.location.href = `/collections/all/vehicle-${encodeURIComponent(customTag)}`;
            return;
        }

        if (matchingFitment.tag) {
            const cleanTag = matchingFitment.tag
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
            window.location.href = `/collections/all/${cleanTag}`;
            return;
        }

        const constructedTag = generateProductTag(vehicle)
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
            
        window.location.href = `/collections/all/${constructedTag}`;

    } catch (error) {
        showError('Error finding products for your vehicle');
    }
}

function processVehicleData(data) {
    const capitalize = str => str?.charAt(0).toUpperCase() + str?.slice(1)?.toLowerCase();
    const { ModelData: m = {}, VehicleIdentification: v = {}, 
            Transmission: t = {}, BodyDetails: b = {}, SmmtDetails: s = {} } = data;
    
    // Get full model name
    let modelName = (m.Range || v.DvlaModel || '')
        .toLowerCase()
        .replace(/\s+limited\s+.*$/i, '')  // Remove 'limited' and anything after
        .replace(/\s+ecoblue\s+.*$/i, '') // Remove 'ecoblue' and anything after
        .replace(/\s+\d{3}.*$/i, ''); // Remove model numbers and anything after
    
    // Special handling for Transit Custom
    if (modelName.includes('transit')) {
        modelName = modelName.replace(/transit\s+/, 'transit '); // Normalize spacing
        if (modelName.includes('custom')) {
            modelName = 'transit custom';
        }
    }
    
    // Determine vehicle type and appropriate cab type
    let cabType = null;
    
    // First check if it's a commercial vehicle (van or pickup)
    const isCommercial = b.BodyStyle?.toLowerCase().includes('pick up') || 
                        s.BodyStyle?.toLowerCase().includes('pick up') ||
                        modelName.includes('ranger') ||
                        modelName.includes('hilux') ||
                        modelName.includes('transit');
    
    if (isCommercial) {
        // For commercial vehicles, handle wheelbase and cab types
        if (b.BodyStyle?.toLowerCase().includes('pick up') || s.BodyStyle?.toLowerCase().includes('pick up')) {
            // For pickups, prioritize cab type
            if (m.CabType?.toLowerCase().includes('double') || s.CabType?.toLowerCase().includes('double')) {
                cabType = 'Double Cab';
            } else if (m.CabType?.toLowerCase().includes('single') || s.CabType?.toLowerCase().includes('single')) {
                cabType = 'Single Cab';
            } else if (m.CabType?.toLowerCase().includes('extra') || s.CabType?.toLowerCase().includes('extra')) {
                cabType = 'Extra Cab';
            }
        } else {
            // For vans, use wheelbase type
            const wheelbaseType = b.WheelBaseType || s.Wheelbase;
            if (wheelbaseType) {
                const wheelbaseLower = wheelbaseType.toLowerCase();
                if (wheelbaseLower.includes('short')) {
                    cabType = 'SWB';
                } else if (wheelbaseLower.includes('long')) {
                    cabType = 'LWB';
                } else if (wheelbaseLower.includes('medium')) {
                    cabType = 'MWB';
                } else if (wheelbaseLower.includes('extra')) {
                    cabType = 'XLWB';
                }
            }
        }
    } else {
        // For cars, use body style
        const bodyStyle = b.BodyStyle || s.BodyStyle;
        if (bodyStyle) {
            const bodyStyleLower = bodyStyle.toLowerCase();
            if (bodyStyleLower.includes('saloon')) {
                cabType = 'Saloon';
            } else if (bodyStyleLower.includes('estate')) {
                cabType = 'Estate';
            } else if (bodyStyleLower.includes('hatchback')) {
                cabType = 'Hatchback';
            } else if (bodyStyleLower.includes('suv')) {
                cabType = 'SUV';
            } else if (bodyStyleLower.includes('coupe')) {
                cabType = 'Coupe';
            } else if (bodyStyleLower.includes('convertible')) {
                cabType = 'Convertible';
            }
        }
    }
    
    // Get the transmission type from SmmtDetails first, then fallback
    let transmission = null;
    if (s && s.Transmission) {
        transmission = s.Transmission.toLowerCase();
    } else if (t && t.TransmissionType) {
        transmission = t.TransmissionType.toLowerCase();
    }
    
    // Get trim information
    let trim = null;
    if (m.ModelVariant) {
        // For BMW, look for M Sport in the model variant
        if (m.Make?.toLowerCase() === 'bmw' && m.ModelVariant.toLowerCase().includes('m sport')) {
            trim = 'M Sport';
        } else {
            // For other makes, use the first part of the model variant
            trim = capitalize(m.ModelVariant.split(' ')[0]);
        }
    }
    
    return {
        make: (m.Make || v.DvlaMake).toUpperCase(), // Convert make to uppercase
        model: modelName.trim(),
        trim: trim,
        cabType: cabType,
        year: v.YearOfManufacture || new Date().getFullYear(),
        transmission: transmission,
        fuelType: s.FuelType ? capitalize(s.FuelType) : null
    };
}

function matchFitment(vehicleData, fitment) {
    // Basic matching criteria
    const yearMatches = +vehicleData.year >= +fitment.yearFrom && +vehicleData.year <= +fitment.yearTo;

    // For trim, we need an exact match if we have a trim from the API
    const trimMatches = !vehicleData.trim || !fitment.lowercaseTrim ? true :
        fitment.lowercaseTrim === vehicleData.trim.toLowerCase();
    
    // For cab type, we need an exact match if we have a cab type from the API
    let cabTypeMatches = !vehicleData.cabType || !fitment.cabType;
    
    if (vehicleData.cabType && fitment.cabType) {
        const vCabType = vehicleData.cabType.toLowerCase();
        const fCabType = fitment.cabType.toLowerCase();

        // Check for exact match first
        if (vCabType === fCabType) {
            cabTypeMatches = true;
        } 
        // Then check for abbreviation matches
        else if (
            (vCabType === 'swb' && fCabType === 'short wheelbase') ||
            (vCabType === 'lwb' && fCabType === 'long wheelbase') ||
            (vCabType === 'mwb' && fCabType === 'medium wheelbase') ||
            (vCabType === 'xlwb' && fCabType === 'extra long wheelbase') ||
            (vCabType === 'short wheelbase' && fCabType === 'swb') ||
            (vCabType === 'long wheelbase' && fCabType === 'lwb') ||
            (vCabType === 'medium wheelbase' && fCabType === 'mwb') ||
            (vCabType === 'extra long wheelbase' && fCabType === 'xlwb')
        ) {
            cabTypeMatches = true;
        }
    }
    
    // For transmission and fuel type, only match if both values exist
    const transmissionMatches = !vehicleData.transmission || !fitment.transmission ? true :
        fitment.transmission.toLowerCase() === vehicleData.transmission.toLowerCase();
    
    const fuelTypeMatches = !vehicleData.fuelType || !fitment.fuelType ? true :
        fitment.fuelType.toLowerCase() === vehicleData.fuelType.toLowerCase();
    
    // Model matching - extract model from tag if model property is not available
    let modelMatches = false;
    if (fitment.model) {
        modelMatches = fitment.model.toLowerCase() === vehicleData.model.toLowerCase();
    } else if (fitment.tag) {
        // Extract model from tag (format: bmw-5 series-m sport-2009-2017)
        const tagParts = fitment.tag.split('-');
        if (tagParts.length >= 2) {
            const tagModel = tagParts[1].replace(/\s+/g, ' ').trim();
            modelMatches = tagModel.toLowerCase() === vehicleData.model.toLowerCase();
        }
    }
    
    // For manual dropdowns, we only need to match the fields that are actually selected
    const requiredMatches = [
        yearMatches,
        modelMatches,
        // Only include trim match if trim is selected
        vehicleData.trim ? trimMatches : true,
        // Only include cab type match if cab type is selected
        vehicleData.cabType ? cabTypeMatches : true,
        // Only include transmission match if transmission is selected
        vehicleData.transmission ? transmissionMatches : true,
        // Only include fuel type match if fuel type is selected
        vehicleData.fuelType ? fuelTypeMatches : true
    ];
    
    // All required matches must be true
    return requiredMatches.every(match => match === true);
}

function showNoProductsMessage() {
    const popup = document.getElementById('no-products-popup');
    if (!popup) return;
    
    popup.style.display = 'flex';
    
    // Close button handler
    const closeButton = popup.querySelector('.vehicle-search__popup-close');
    if (closeButton) {
        closeButton.addEventListener('click', function() {
            popup.style.display = 'none';
        });
    }
    
    // Close on click outside
    popup.addEventListener('click', function(e) {
        if (e.target === popup) {
            popup.style.display = 'none';
        }
    });
    
    // Close on escape key
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && popup.style.display === 'flex') {
            popup.style.display = 'none';
        }
    });
}

function showError(message) {
    const errorDiv = document.getElementById('reg-search-error');
    if (errorDiv) errorDiv.textContent = message;
}

function saveVehicle(vehicle) {
    try {
        const savedVehicles = JSON.parse(localStorage.getItem('savedVehicles') || '[]');
        
        if (!savedVehicles.some(v => 
            v.make === vehicle.make && 
            v.model === vehicle.model && 
            v.year === vehicle.year
        )) {
            savedVehicles.push(vehicle);
            localStorage.setItem('savedVehicles', JSON.stringify(savedVehicles));
        }
    } catch (error) {
    }
}

function generateProductTag(vehicle) {
    const parts = [
        vehicle.make,
        vehicle.model,
        vehicle.trim,
        vehicle.cabType,
        vehicle.year
    ].filter(Boolean); // Remove any null/undefined values
    
    return parts.join('-')
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

function capitalize(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
}

function addListeners() {
    const elements = window.VehicleSearchApp.elements;
    
    // Add tab switching listeners
    elements.tabButtons.forEach(button => {
        button.addEventListener('click', function() {
            const tabId = this.getAttribute('data-tab');
            switchTab(tabId);
        });
    });

    // Add other listeners
    elements.desktopTriggers.forEach(trigger => {
        trigger.addEventListener('click', openVehicleSearch);
    });
    
    if (elements.mobileTrigger) {
        elements.mobileTrigger.addEventListener('click', openVehicleSearch);
    }
    
    elements.closeButtons.forEach(button => {
        button.addEventListener('click', closeVehicleSearch);
    });
    
    elements.vehicleSearch.addEventListener('click', (e) => {
        if (e.target === elements.vehicleSearch) {
            closeVehicleSearch();
        }
    });
    
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && elements.vehicleSearch.classList.contains('active')) {
            closeVehicleSearch();
        }
    });
}

function closeVehicleSearch() {
    const elements = window.VehicleSearchApp.elements;
    elements.vehicleSearch.classList.remove('active');
    document.body.classList.remove('vehicle-search-open');
}

function switchTab(tabId) {
    // Hide all tab contents
    document.querySelectorAll('.vehicle-search__option').forEach(content => {
        content.classList.remove('active');
    });
    
    // Remove active class from all tabs
    document.querySelectorAll('.tab-button').forEach(tab => {
        tab.classList.remove('active');
    });
    
    // Show selected tab content and activate tab
    const tabContent = document.getElementById(`${tabId}-tab`);
    const tabButton = document.querySelector(`[data-tab="${tabId}"]`);
    
    if (tabContent) {
        tabContent.classList.add('active');
        // If switching to saved tab, load saved vehicles
        if (tabId === 'saved') {
            loadSavedVehicles();
        }
    }
    if (tabButton) {
        tabButton.classList.add('active');
    }
}

function loadSavedVehicles() {
    const savedVehiclesList = document.getElementById('saved-vehicles-list');
    const emptyMessage = document.getElementById('empty-vehicles-message');
    
    try {
        const savedVehicles = JSON.parse(localStorage.getItem('savedVehicles') || '[]');
        
        if (!savedVehiclesList || !emptyMessage) {
            return;
        }
        
        if (savedVehicles.length === 0) {
            savedVehiclesList.style.display = 'none';
            emptyMessage.style.display = 'block';
            return;
        }
        
        savedVehiclesList.style.display = 'block';
        emptyMessage.style.display = 'none';
        
        const vehicleHTML = savedVehicles.map((vehicle, index) => `
            <div class="saved-vehicle-item">
                <div class="saved-vehicle-info">
                    <div class="saved-vehicle-name">${vehicle.make} ${vehicle.model}</div>
                    <div class="saved-vehicle-details">
                        ${vehicle.year}
                        ${vehicle.trim ? ` • ${vehicle.trim}` : ''}
                        ${vehicle.cabType ? ` • ${vehicle.cabType}` : ''}
                        ${vehicle.transmission ? ` • ${vehicle.transmission}` : ''}
                        ${vehicle.fuelType ? ` • ${vehicle.fuelType}` : ''}
                    </div>
                </div>
                <div class="saved-vehicle-actions">
                    <button class="saved-vehicle-browse" onclick="handleVehicleMatch(${JSON.stringify(vehicle).replace(/"/g, '&quot;')})">
                        Browse Parts
                    </button>
                    <button class="saved-vehicle-remove" onclick="removeSavedVehicle(${index})">
                        Remove
                    </button>
                </div>
            </div>
        `).join('');
        
        savedVehiclesList.innerHTML = vehicleHTML;
        
    } catch (error) {
        if (savedVehiclesList) savedVehiclesList.style.display = 'none';
        if (emptyMessage) emptyMessage.style.display = 'block';
    }
}

function removeSavedVehicle(index) {
    try {
        const savedVehicles = JSON.parse(localStorage.getItem('savedVehicles') || '[]');
        savedVehicles.splice(index, 1);
        localStorage.setItem('savedVehicles', JSON.stringify(savedVehicles));
        loadSavedVehicles(); // Refresh the display
    } catch (error) {
    }
}

// Add this new function to update number bubbles
function updateNumberBubbles() {
    const dropdowns = document.querySelector('.vehicle-search__dropdowns');
    if (!dropdowns) {
        return;
    }

    const visibleFields = Array.from(dropdowns.querySelectorAll('.field-group')).filter(field => {
        const display = window.getComputedStyle(field).display;
        return display !== 'none';
    });
    
    visibleFields.forEach((field, index) => {
        const bubble = field.querySelector('.number-bubble');
        if (bubble) {
            bubble.textContent = (index + 1).toString();
            bubble.style.display = 'flex'; // Ensure bubble is visible
        }
    });
}